import React, { useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from "@/components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";
import {
	Upload,
	FileText,
	Eye,
	X,
	Edit3,
	Check,
	Trash2,
	BookOpen,
	Users,
	User,
} from "lucide-react";
import { toast } from "sonner";
import {
	OrgaAdminKlausurService,
	type Klausur,
} from "@/lib/orgaAdmin/orgaAdminKlausur.service";
import type { OrgaAdminGruppeDTO } from "@/lib/orgaAdmin/orgaAdmin.types";

interface UploadedFile {
	id: string;
	file: File;
	originalName: string;
	displayName: string;
	previewUrl: string | null;
	isEditing: boolean;
	selectedGruppeId: string;
}

interface SingleKlausurUploadProps {
	klausur: Klausur;
	groups: OrgaAdminGruppeDTO[];
	selectedOrganizationId: string;
	isLoadingGroups: boolean;
	onUploadComplete: () => void;
	onFilesChanged?: (hasFiles: boolean) => void;
}

interface PDFPreviewDialogProps {
	file: File | null;
	isOpen: boolean;
	onClose: () => void;
}

function PDFPreviewDialog({ file, isOpen, onClose }: PDFPreviewDialogProps) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	React.useEffect(() => {
		if (file && isOpen) {
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);
			return () => {
				URL.revokeObjectURL(url);
				setPreviewUrl(null);
			};
		}
	}, [file, isOpen]);

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] p-0 flex flex-col">
				<DialogHeader className="px-6 py-4">
					<DialogTitle>PDF Vorschau: {file?.name}</DialogTitle>
					<DialogDescription>
						Vorschau der ausgewählten PDF-Datei
					</DialogDescription>
				</DialogHeader>
				<div className="flex-1 overflow-hidden">
					{previewUrl ? (
						<iframe
							src={previewUrl}
							className="w-full h-full"
							title="PDF Preview"
						/>
					) : (
						<div className="flex items-center justify-center h-full">
							<div className="text-center">
								<FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
								<p className="text-muted-foreground">
									PDF-Vorschau wird geladen...
								</p>
							</div>
						</div>
					)}
				</div>
				<DialogFooter className="px-6 py-4 border-t">
					<Button variant="outline" onClick={onClose}>
						Schließen
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export default function SingleKlausurUpload({
	klausur,
	groups,
	selectedOrganizationId,
	isLoadingGroups,
	onUploadComplete,
	onFilesChanged,
}: SingleKlausurUploadProps) {
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [selectedGruppeId, setSelectedGruppeId] = useState<string>("");
	const [groupMode, setGroupMode] = useState<"global" | "perFile" | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [previewFile, setPreviewFile] = useState<File | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);
	const [showUnsavedEditDialog, setShowUnsavedEditDialog] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const groupSelectionRef = useRef<HTMLDivElement>(null);
	const uploadedFilesRef = useRef<HTMLDivElement>(null);
	const previousUploadCountRef = useRef(0);

	// Notify parent when files change
	React.useEffect(() => {
		onFilesChanged?.(uploadedFiles.length > 0);
	}, [uploadedFiles.length, onFilesChanged]);

	React.useEffect(() => {
		if (groupMode === "global" && selectedGruppeId) {
			setUploadedFiles((prev) =>
				prev.map((file) => ({
					...file,
					selectedGruppeId: selectedGruppeId,
				})),
			);
		}
	}, [groupMode, selectedGruppeId]);

	React.useEffect(() => {
		if (uploadedFiles.length > previousUploadCountRef.current) {
			groupSelectionRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
		previousUploadCountRef.current = uploadedFiles.length;
	}, [uploadedFiles.length]);

	const step1Complete = uploadedFiles.length > 0;
	const step2Complete =
		groupMode === "perFile"
			? true
			: groupMode === "global" && !!selectedGruppeId;
	const step3Complete =
		groupMode === "perFile"
			? uploadedFiles.length > 0 &&
				uploadedFiles.every((file) => file.selectedGruppeId)
			: uploadedFiles.length > 0 && !!selectedGruppeId;
	const completedSteps = [step1Complete, step2Complete, step3Complete].filter(
		Boolean,
	).length;
	const progressPercent = Math.round((completedSteps / 3) * 100);
	const currentStep = !step1Complete ? 1 : !step2Complete ? 2 : 3;
	const currentStepLabel =
		currentStep === 1
			? "PDFs hinzufügen"
			: currentStep === 2
				? "Zuordnung wählen"
				: "Prüfen & hochladen";

	const processFiles = useCallback(
		(files: FileList) => {
			const newFiles: UploadedFile[] = [];

			for (let i = 0; i < files.length; i++) {
				const file = files[i];

				// Validate file type
				if (file.type !== "application/pdf") {
					toast.error(`${file.name} ist keine PDF-Datei und wird übersprungen`);
					continue;
				}

				const uploadedFile: UploadedFile = {
					id: `${Date.now()}_${i}`,
					file,
					originalName: file.name,
					displayName: file.name,
					previewUrl: null,
					isEditing: false,
					selectedGruppeId: selectedGruppeId,
				};

				newFiles.push(uploadedFile);
			}

			setUploadedFiles((prev) => [...prev, ...newFiles]);
			toast.success(`${newFiles.length} Dateien hinzugefügt`);
		},
		[selectedGruppeId],
	);

	const handleFileSelection = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const files = event.target.files;
			if (!files) return;

			processFiles(files);

			// Clear file input
			event.target.value = "";
			if (fileInputRef.current) fileInputRef.current.value = "";
		},
		[processFiles],
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragOver(false);

			const files = e.dataTransfer.files;
			if (files.length > 0) {
				processFiles(files);
			}
		},
		[processFiles],
	);

	const handleRemoveFile = useCallback((fileId: string) => {
		setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
	}, []);

	const handleClearAll = useCallback(() => {
		setUploadedFiles([]);
	}, []);

	const handleEditName = useCallback((fileId: string) => {
		setUploadedFiles((prev) =>
			prev.map((f) => (f.id === fileId ? { ...f, isEditing: true } : f)),
		);
	}, []);

	const handleSaveName = useCallback((fileId: string, newName: string) => {
		setUploadedFiles((prev) =>
			prev.map((f) =>
				f.id === fileId ? { ...f, displayName: newName, isEditing: false } : f,
			),
		);
	}, []);

	const handleCancelEdit = useCallback((fileId: string) => {
		setUploadedFiles((prev) =>
			prev.map((f) => (f.id === fileId ? { ...f, isEditing: false } : f)),
		);
	}, []);

	const handlePreview = useCallback((file: File) => {
		setPreviewFile(file);
		setIsPreviewOpen(true);
	}, []);

	const handleGruppeChangeForFile = useCallback(
		(fileId: string, gruppeId: string) => {
			setUploadedFiles((prev) =>
				prev.map((f) =>
					f.id === fileId ? { ...f, selectedGruppeId: gruppeId } : f,
				),
			);
		},
		[],
	);

	const handleUploadAll = useCallback(
		async (forceUpload = false) => {
			if (uploadedFiles.length === 0) {
				toast.error("Keine Dateien zum Hochladen ausgewählt");
				return;
			}

			// Check if any file is being edited
			const hasEditingFile = uploadedFiles.some((f) => f.isEditing);
			if (hasEditingFile && !forceUpload) {
				setShowUnsavedEditDialog(true);
				return;
			}

			// Validate groups
			if (!groupMode) {
				toast.error("Bitte wählen Sie zuerst eine Zuordnung");
				return;
			}
			if (groupMode === "perFile") {
				const filesWithoutGroup = uploadedFiles.filter(
					(f) => !f.selectedGruppeId,
				);
				if (filesWithoutGroup.length > 0) {
					toast.error(
						"Bitte wählen Sie für alle Dateien eine Gruppe/Ausschuss aus",
					);
					return;
				}
			} else {
				if (!selectedGruppeId) {
					toast.error("Bitte wählen Sie eine Gruppe aus");
					return;
				}
			}

			setIsUploading(true);
			setUploadProgress(0);

			try {
				const service = OrgaAdminKlausurService.getInstance();

				let successCount = 0;
				let errorCount = 0;

				// Upload files one by one
				for (let index = 0; index < uploadedFiles.length; index++) {
					const uploadedFile = uploadedFiles[index];
					try {
						const internalIdentifier = uploadedFile.displayName;
						const gruppeId =
							groupMode === "perFile"
								? uploadedFile.selectedGruppeId
								: selectedGruppeId;

						await service.createStudentKlausur(
							selectedOrganizationId,
							klausur.id,
							uploadedFile.file,
							internalIdentifier,
							gruppeId,
						);

						successCount++;
					} catch (error) {
						console.error(
							`Error uploading ${uploadedFile.displayName}:`,
							error,
						);
						errorCount++;
					} finally {
						setUploadProgress(
							Math.round(((index + 1) / uploadedFiles.length) * 100),
						);
					}
				}

				if (successCount > 0) {
					toast.success(
						`${successCount} Studentenklausuren erfolgreich hochgeladen`,
					);
					setUploadedFiles([]);
					onUploadComplete();
				}

				if (errorCount > 0) {
					toast.error(`${errorCount} Dateien konnten nicht hochgeladen werden`);
				}
			} catch (error) {
				console.error("Error during upload:", error);
				toast.error("Fehler beim Hochladen der Dateien");
			} finally {
				setIsUploading(false);
				setUploadProgress(0);
			}
		},
		[
			uploadedFiles,
			selectedGruppeId,
			groupMode,
			selectedOrganizationId,
			klausur.id,
			onUploadComplete,
		],
	);

	const selectedGroup = groups.find((g) => g.id === selectedGruppeId);

	return (
		<div className="w-full space-y-6">
			{/* Progress */}
			<div className="space-y-2">
				<div className="flex items-center justify-between text-xs text-muted-foreground">
					<span>Schritt {currentStep}/3</span>
					<span>{currentStepLabel}</span>
				</div>
				<div className="relative">
					<div className="absolute top-3 left-0 right-0 h-px bg-muted -z-10" />
					<div
						className="absolute top-3 left-0 h-px bg-primary transition-all duration-500 -z-10"
						style={{ width: `${progressPercent}%` }}
					/>
					<div className="flex justify-between relative z-10">
						{[
							{ step: 1, label: "Dateien", done: step1Complete },
							{ step: 2, label: "Zuordnung", done: step2Complete },
							{ step: 3, label: "Upload", done: step3Complete },
						].map((s) => (
							<div key={s.step} className="flex flex-col items-center gap-1">
								<div
									className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-[10px] font-semibold transition-colors ${
										s.done
											? "bg-primary border-primary text-primary-foreground"
											: "bg-background border-muted text-muted-foreground"
									}`}
								>
									{s.done ? <Check className="h-3 w-3" /> : s.step}
								</div>
								<span
									className={`text-[11px] ${
										s.done ? "text-primary" : "text-muted-foreground"
									}`}
								>
									{s.label}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Klausur Info */}
			<Card className="p-4 bg-primary/5 border-primary/20">
				<div className="flex items-center gap-3">
					<BookOpen className="h-5 w-5 text-primary" />
					<div>
						<h3 className="font-semibold text-lg">{klausur.title}</h3>
						<p className="text-sm text-muted-foreground">
							Index: {klausur.index} • Bereits{" "}
							{klausur.studentKlausuren?.length || 0} Studentenklausuren
						</p>
					</div>
				</div>
			</Card>

			{/* Step 1: File Upload */}
			<div className="space-y-2">
				<h3 className="text-sm font-semibold text-muted-foreground">
					1. PDFs hochladen
				</h3>
				<button
					type="button"
					className={`relative w-full border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer ${
						isDragOver
							? "border-primary bg-primary/5"
							: "border-gray-300 hover:border-gray-400"
					}`}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					aria-label="Upload PDF files"
				>
					<Input
						ref={fileInputRef}
						type="file"
						accept=".pdf,application/pdf"
						multiple
						onChange={handleFileSelection}
						className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
					/>
					<div className="flex flex-col items-center gap-3">
						<div
							className={`p-3 rounded-full ${isDragOver ? "bg-primary/10" : "bg-slate-100"}`}
						>
							<Upload
								className={`h-6 w-6 ${isDragOver ? "text-primary" : "text-slate-600"}`}
							/>
						</div>
						<div className="text-center">
							<p className="text-sm font-medium text-slate-900">
								{isDragOver
									? "PDF-Dateien hier ablegen"
									: "PDF-Dateien hochladen"}
							</p>
							<p className="text-xs text-slate-500 mt-1">
								Dateien hierher ziehen oder klicken zum Auswählen
							</p>
						</div>
					</div>
				</button>
			</div>

			{uploadedFiles.length > 0 && (
				<>
					{/* Step 2: Group Selection */}
					<div className="space-y-3" ref={groupSelectionRef}>
						<h3 className="text-sm font-semibold text-muted-foreground">
							2. Zuordnung wählen
						</h3>

						<div className="grid grid-cols-2 gap-3">
							<div
								className={`
                            relative flex flex-col items-start gap-2 rounded-lg border-2 p-3 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-all
                            ${
															groupMode === "global"
																? "border-primary bg-primary/5"
																: "border-muted bg-transparent"
														}
                        `}
								onClick={() => setGroupMode("global")}
							>
								<div className="flex items-center gap-2 font-medium">
									<Users className="h-4 w-4" />
									<span>Alle gleich</span>
								</div>
								<p className="text-xs text-muted-foreground leading-tight">
									Alle Dateien kommen in dieselbe Gruppe.
								</p>
							</div>

							<div
								className={`
                            relative flex flex-col items-start gap-2 rounded-lg border-2 p-3 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-all
                            ${
															groupMode === "perFile"
																? "border-primary bg-primary/5"
																: "border-muted bg-transparent"
														}
                        `}
								onClick={() => setGroupMode("perFile")}
							>
								<div className="flex items-center gap-2 font-medium">
									<User className="h-4 w-4" />
									<span>Individuell</span>
								</div>
								<p className="text-xs text-muted-foreground leading-tight">
									Für jede Datei eine eigene Gruppe wählen.
								</p>
							</div>
						</div>

						{groupMode === "global" && (
							<div className="space-y-1 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
								<Select
									value={selectedGruppeId}
									onValueChange={setSelectedGruppeId}
									disabled={isLoadingGroups}
								>
									<SelectTrigger className="border-primary/50">
										<SelectValue
											placeholder={
												isLoadingGroups
													? "Gruppen werden geladen..."
													: "Gruppe für alle auswählen..."
											}
										/>
									</SelectTrigger>
									<SelectContent>
										{groups.map((group) => (
											<SelectItem key={group.id} value={group.id}>
												<span>{group.title}</span>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{selectedGroup && (
									<p className="text-xs text-muted-foreground px-1">
										{selectedGroup.users.length} Benutzer*innen •{" "}
										{selectedGroup.studentKlausuren.length} Klausuren
									</p>
								)}
							</div>
						)}

						{groupMode === "perFile" && (
							<p className="text-xs text-muted-foreground px-1 animate-in fade-in slide-in-from-top-2 duration-200">
								Sie können die Gruppe gleich unten für jede Datei einzeln
								festlegen.
							</p>
						)}
					</div>

					{/* Step 3: Review & Upload */}
					<div className="space-y-4" ref={uploadedFilesRef}>
						<div className="flex items-center justify-between">
							<h3 className="text-sm font-semibold text-muted-foreground">
								3. Prüfen & hochladen ({uploadedFiles.length})
							</h3>
						</div>

						<div className="space-y-2 pb-20">
							{uploadedFiles.map((uploadedFile, index) => (
								<FileUploadItem
									key={uploadedFile.id}
									uploadedFile={uploadedFile}
									index={index + 1}
									groups={groups}
									isGroupSelectionDisabled={groupMode === "global"}
									onGruppeSelectOpen={() => {
										if (!groupMode) setGroupMode("perFile");
									}}
									isLoadingGroups={isLoadingGroups}
									onRemove={handleRemoveFile}
									onEditName={handleEditName}
									onSaveName={handleSaveName}
									onCancelEdit={handleCancelEdit}
									onPreview={handlePreview}
									onGruppeChange={handleGruppeChangeForFile}
								/>
							))}
						</div>
					</div>
				</>
			)}

			{/* Sticky Upload Button Bar */}
			{uploadedFiles.length > 0 && (
				<div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-50 rounded-b-lg">
					<div className="max-w-3xl mx-auto px-6 py-4 space-y-3">
						<div className="flex items-center justify-between gap-4">
							<div className="flex items-center gap-3">
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<FileText className="h-4 w-4" />
									<span className="font-medium">{uploadedFiles.length}</span>
									<span>
										{uploadedFiles.length === 1 ? "Datei" : "Dateien"} bereit
									</span>
								</div>
								{!step3Complete && (
									<span className="text-xs text-amber-600 flex items-center gap-1">
										Zuordnung erforderlich
									</span>
								)}
							</div>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={handleClearAll}
									disabled={isUploading}
								>
									Alle entfernen
								</Button>
								<Button
									onClick={() => handleUploadAll(false)}
									disabled={
										isUploading ||
										!groupMode ||
										(groupMode === "global" && !selectedGruppeId) ||
										(groupMode === "perFile" &&
											uploadedFiles.some((f) => !f.selectedGruppeId))
									}
									className="min-w-[150px]"
								>
									{isUploading ? (
										<div className="flex items-center gap-2">
											<div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
											Wird hochgeladen...
										</div>
									) : (
										<div className="flex items-center gap-2">
											<Upload className="h-4 w-4" />
											{uploadedFiles.length === 1
												? "Hochladen"
												: "Alle hochladen"}
										</div>
									)}
								</Button>
							</div>
						</div>
						{isUploading && (
							<div className="space-y-1">
								<div className="flex items-center justify-between text-xs text-muted-foreground">
									<span>Upload läuft</span>
									<span>{uploadProgress}%</span>
								</div>
								<div className="h-2 w-full rounded-full bg-muted overflow-hidden">
									<div
										className="h-full bg-primary transition-all duration-300"
										style={{ width: `${uploadProgress}%` }}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Unsaved Edit Confirmation Dialog */}
			<Dialog
				open={showUnsavedEditDialog}
				onOpenChange={setShowUnsavedEditDialog}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Nicht gespeicherte Änderungen</DialogTitle>
						<DialogDescription>
							Sie bearbeiten gerade einen Dateinamen. Möchten Sie die Änderungen
							speichern oder verwerfen, bevor Sie hochladen?
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setShowUnsavedEditDialog(false)}
						>
							Zurück zur Bearbeitung
						</Button>
						<Button
							variant="default"
							onClick={() => {
								// Cancel all editing states
								setUploadedFiles((prev) =>
									prev.map((f) => ({ ...f, isEditing: false })),
								);
								setShowUnsavedEditDialog(false);
								handleUploadAll(true);
							}}
						>
							Änderungen verwerfen & hochladen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* PDF Preview Dialog */}
			<PDFPreviewDialog
				file={previewFile}
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
			/>
		</div>
	);
}

interface FileUploadItemProps {
	uploadedFile: UploadedFile;
	index: number;
	groups: OrgaAdminGruppeDTO[];
	isGroupSelectionDisabled: boolean;
	isLoadingGroups: boolean;
	onGruppeSelectOpen: () => void;
	onRemove: (fileId: string) => void;
	onEditName: (fileId: string) => void;
	onSaveName: (fileId: string, newName: string) => void;
	onCancelEdit: (fileId: string) => void;
	onPreview: (file: File) => void;
	onGruppeChange: (fileId: string, gruppeId: string) => void;
}

function FileUploadItem({
	uploadedFile,
	index,
	groups,
	isGroupSelectionDisabled,
	isLoadingGroups,
	onGruppeSelectOpen,
	onRemove,
	onEditName,
	onSaveName,
	onCancelEdit,
	onPreview,
	onGruppeChange,
}: FileUploadItemProps) {
	const [editName, setEditName] = useState(uploadedFile.displayName);

	const handleSave = () => {
		onSaveName(uploadedFile.id, editName);
	};

	const handleCancel = () => {
		setEditName(uploadedFile.displayName);
		onCancelEdit(uploadedFile.id);
	};

	return (
		<Card className="p-3">
			<div className="flex items-start gap-3 min-w-0">
				<div className="flex items-center gap-2 flex-shrink-0 pt-1">
					<span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full w-5 h-5 flex items-center justify-center">
						{index}
					</span>
					<FileText className="h-6 w-6 text-blue-500" />
				</div>

				<div className="flex-1 space-y-2 min-w-0">
					{/* File Name */}
					<div className="space-y-1">
						{uploadedFile.isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									value={editName}
									onChange={(e) => setEditName(e.target.value)}
									className="flex-1 h-8 text-sm"
									placeholder="Dateiname eingeben..."
								/>
								<Button size="sm" className="h-8 w-8 p-0" onClick={handleSave}>
									<Check className="h-4 w-4" />
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="h-8 w-8 p-0"
									onClick={handleCancel}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<div className="flex items-center gap-2">
								<h4 className="font-medium text-sm truncate">
									{uploadedFile.displayName}
								</h4>
								<Button
									size="sm"
									variant="ghost"
									className="h-6 w-6 p-0"
									onClick={() => onEditName(uploadedFile.id)}
								>
									<Edit3 className="h-3 w-3" />
								</Button>
							</div>
						)}
						<p className="text-xs text-muted-foreground truncate">
							{uploadedFile.originalName} •{" "}
							{(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
						</p>
					</div>

					{/* Gruppe pro Datei */}
					<div className="space-y-1">
						<Select
							value={uploadedFile.selectedGruppeId}
							onValueChange={(value) => onGruppeChange(uploadedFile.id, value)}
							disabled={isLoadingGroups || isGroupSelectionDisabled}
							onOpenChange={(open) => {
								if (open) onGruppeSelectOpen();
							}}
						>
							<SelectTrigger className="h-8 text-sm">
								<SelectValue
									placeholder={
										isLoadingGroups ? "Laden..." : "Gruppe auswählen..."
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{groups.map((group) => (
									<SelectItem key={group.id} value={group.id}>
										<span className="text-sm">{group.title}</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex flex-col gap-1 flex-shrink-0">
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onPreview(uploadedFile.file)}
						className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
					>
						<Eye className="h-4 w-4" />
					</Button>
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onRemove(uploadedFile.id)}
						className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</Card>
	);
}
